import i18nFactory from '@yandex-int/i18n';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import {
    setCreativePreviewPopupData,
    UPDATE_PCODE_PREVIEW_CREATIVE_POPUP,
    updatePcodePreviewPopup,
    UpdatePcodePreviewPopup
} from '../../actions/bannerCreativePreviewPopup';
import { updateBannerEditImageFormats } from '../../actions/bannersEdit';
import {
    clearImageSmartCenterEditor,
    fillImageSmartCenterEditorData,
    OPEN_IMAGE_SMART_CENTER_EDITOR,
    OpenImageSmartCenterEditorAction,
    SAVE_IMAGE_SMART_CENTER,
    setImageSmartCenterEditorError
} from '../../actions/imageSmartCenterEditor';
import { mapBannerEditDataToPcodeRenderData } from '../../mappers/banner/banner';
import { mapImageSmartCenterData, mapImageSmartCentersDataToApiInput } from '../../mappers/smartCenters';
import {
    fillBannerEmptyFieldsForPreview,
    mapAdImageFormatsFragmentToBannerImageFormat
} from '../../service/BannerHelpers/BannerHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { getEditedBannerWithImage } from '../../typings/bannersEditState';
import { UpdateImageSmartCenters } from '../../typings/graphql-grid';
import { PcodeAds, PcodeRenderData } from '../../typings/pcode';
import { State } from '../../typings/state';

import * as keyset from './imageSmartCenterEditor.i18n';

const i18n = i18nFactory(keyset);

export const openImageSmartCenterEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_IMAGE_SMART_CENTER_EDITOR),
        mergeMap((action: OpenImageSmartCenterEditorAction) => {
            const banner = getEditedBannerWithImage(store$.value.editBanners.banners[action.bannerId]);
            if (!banner) {
                return EMPTY;
            }
            const image = store$.value.imageEditor[action.bannerId].image;

            return of(fillImageSmartCenterEditorData(mapImageSmartCenterData(banner, image)));
        })
    );
};

export const saveImageSmartCentersEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
): Observable<Action> => {
    return action$.pipe(
        ofType(SAVE_IMAGE_SMART_CENTER),
        mergeMap(
            (): Observable<Action> => {
                const {
                    imageSmartCenterEditor: { data },
                    commonData: { login }
                } = store$.value;
                if (!data) {
                    return EMPTY;
                }

                return from(
                    deps.bannersMutationExecutor.updateImageSmartCenters(
                        mapImageSmartCentersDataToApiInput(data),
                        login
                    )
                ).pipe(
                    mergeMap(({ updateSmartCenters: response }: UpdateImageSmartCenters): Action[] => {
                        const { image } = response;
                        if (!image || !image.formats) {
                            throw new Error();
                        }
                        return [
                            updateBannerEditImageFormats(
                                data.bannerId,
                                mapAdImageFormatsFragmentToBannerImageFormat(image.formats)
                            ),
                            updatePcodePreviewPopup(data.bannerId),
                            clearImageSmartCenterEditor()
                        ];
                    }),
                    catchError(
                        (): Observable<Action> => {
                            return of(setImageSmartCenterEditorError(i18n('error')));
                        }
                    )
                );
            }
        )
    );
};

const addDefaultPreviewDataToEmptyBanner = (pcodeData: PcodeRenderData): PcodeRenderData => {
    pcodeData.ads = pcodeData.ads.map((ad: PcodeAds) => {
        return fillBannerEmptyFieldsForPreview(ad);
    });

    return pcodeData;
};

export const updateImagesSmartCentresEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(UPDATE_PCODE_PREVIEW_CREATIVE_POPUP),
        mergeMap(
            (action: UpdatePcodePreviewPopup): Observable<Action> => {
                const state = store$.value;

                const { pcodeRenderData, creativeUrl, previewPopupVisibleType, id } = state.bannerCreativePreviewPopup;
                if (!pcodeRenderData || !creativeUrl || !previewPopupVisibleType || !id) {
                    return EMPTY;
                }

                const banner = state.editBanners.banners[action.adId];
                const image = state.imageEditor[action.adId].image;

                const bannerData = addDefaultPreviewDataToEmptyBanner(
                    mapBannerEditDataToPcodeRenderData(banner, pcodeRenderData.type, image)
                );

                return of(setCreativePreviewPopupData(bannerData, previewPopupVisibleType, creativeUrl, id));
            }
        )
    );
};
